import React, { useEffect, useRef } from 'react';
import '@/App.css';

// Image URLs
const IMAGES = {
  heroTransparent: 'https://customer-assets.emergentagent.com/job_canine-signals/artifacts/5yenwwqh_Hero%20Me%20%28600%20x%20500%20px%29.png',
  walkingTransparent: 'https://customer-assets.emergentagent.com/job_canine-signals/artifacts/w2pqjn9n_Me%20Dog%20Train.png',
  plants: 'https://customer-assets.emergentagent.com/job_e904563e-d603-4fc9-b664-1048981010d6/artifacts/wnaclbma_Retro%20Rebel%2070%20me%20dog.png',
  desk: 'https://customer-assets.emergentagent.com/job_e904563e-d603-4fc9-b664-1048981010d6/artifacts/vyq4e0d1_Private%20session%20Radiant%20Society%201.jpeg',
};

const HELLO_AUDIO_LINK = 'https://app.helloaudio.fm/feed/ab1449fe-b35c-4ad6-9a39-a90dab8fc682/signup';

// Intersection Observer hook
const useIntersectionObserver = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = React.useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isVisible];
};

// Header
const Header = () => {
  return (
    <header 
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-40 bg-[#F9F5F0]/95 backdrop-blur-sm border-b border-[#C25E00]/10"
    >
      <div className="container mx-auto px-6 py-3">
        <div className="flex items-center justify-center md:justify-start">
          <span style={{ fontFamily: 'Sacramento, cursive' }} className="text-2xl md:text-3xl text-[#1C1917]">
            Life Well Played <span className="text-[#C25E00]">|</span> Jt Clough
          </span>
        </div>
      </div>
    </header>
  );
};

// Hero Section - with gradient fade at bottom 1/4
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section" 
      className="bg-[#F9F5F0] pt-20 pb-0 relative"
    >
      <div className="container mx-auto px-6">
        {/* Headline ABOVE image */}
        <h1 className="font-['Fraunces'] text-4xl sm:text-5xl md:text-6xl font-light tracking-tight leading-[1.1] text-[#1C1917] text-center">
          What Your Dog Is Telling You
        </h1>
        
        {/* Image with gradient fade at bottom */}
        <div className="mt-4 flex justify-center relative">
          <div className="relative">
            <img 
              src={IMAGES.heroTransparent}
              alt="Jt Clough with Weimaraner"
              className="h-48 md:h-64 w-auto object-contain relative z-10"
              loading="eager"
            />
            {/* Gradient overlay - starts at 75% from top, fades bottom 25% into next section color */}
            <div 
              className="absolute inset-0 z-20 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom, transparent 0%, transparent 75%, #F2ECE4 100%)'
              }}
            />
          </div>
        </div>
        
        {/* Subtitle and button BELOW image */}
        <div className="mt-2 text-center relative z-30">
          <p className="text-lg md:text-xl leading-relaxed font-medium text-[#1C1917] max-w-md mx-auto">
            Your dog might be showing you something you've been too busy to see.
          </p>
          
          <div className="mt-5 pb-4">
            <a
              href={HELLO_AUDIO_LINK}
              data-testid="hero-cta-button"
              className="cta-button bg-[#C25E00] text-[#FFFCF8] hover:bg-[#A04D00] rounded-full px-8 py-3.5 text-lg font-medium shadow-lg inline-block"
            >
              Listen In
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Noticing Section - minimal top padding to connect with hero gradient
const NoticingSection = () => {
  const statements = [
    "Your dog can't seem to settle. Even when nothing's happening, they're watching, waiting, on alert.",
    "Walks feel like being pulled through your own life. Rushed, reactive, no space to breathe.",
    "They get loud when you haven't been. Barking at the door, the window, the silence.",
    "They won't let you out of their sight. And honestly? You're not sure when you last felt safe enough to be alone either.",
    "They're 'good.' Almost too good. Compliant. Quiet. Careful not to take up space."
  ];

  return (
    <section 
      id="noticing"
      data-testid="noticing-section" 
      className="pt-2 pb-8 md:pb-10 bg-[#F2ECE4]"
    >
      <div className="container mx-auto px-6">
        <p className="italic text-lg md:text-xl text-center mb-8 md:mb-10 text-[#57534E]">
          You might be noticing things like...
        </p>
        
        <div className="max-w-2xl mx-auto space-y-6 md:space-y-8">
          {statements.map((statement, index) => (
            <StatementItem key={index} text={statement} delay={index * 100} />
          ))}
        </div>
        
        <div className="mt-8 md:mt-10 text-center space-y-1">
          <p className="text-[#57534E] text-lg md:text-xl font-light">
            There's no single meaning here.
          </p>
          <p className="text-[#57534E] text-lg md:text-xl font-light">
            These are simply places to notice.
          </p>
        </div>
      </div>
    </section>
  );
};

// Statement Item
const StatementItem = ({ text, delay }) => {
  const [ref, isVisible] = useIntersectionObserver();
  
  return (
    <div 
      ref={ref}
      className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <p className="text-lg md:text-xl leading-relaxed text-[#1C1917] font-light text-left">
        {text}
      </p>
    </div>
  );
};

// Mirror Section - Editorial magazine (cover)
const MirrorSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  
  return (
    <section 
      data-testid="mirror-section" 
      className="py-8 md:py-10 bg-[#F9F5F0] overflow-hidden"
    >
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center">
          {/* Editorial Magazine - Cover */}
          <div className="relative order-2 md:order-1 flex justify-center">
            <div className="relative max-w-[280px] md:max-w-[320px]">
              <div className="absolute -inset-3 bg-[#1C1917] rounded-sm" />
              <div className="relative bg-[#F2ECE4] p-3">
                <div className="text-center mb-2 border-b border-[#1C1917]/20 pb-2">
                  <span className="font-['Fraunces'] text-sm md:text-base tracking-[0.15em] uppercase text-[#1C1917]">
                    Signals We Miss
                  </span>
                </div>
                <div className="relative">
                  <img 
                    src={IMAGES.desk}
                    alt="Woman with Weimaraner"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
                <div className="mt-3 pt-3 border-t border-[#1C1917]/20">
                  <p className="font-['Fraunces'] text-base md:text-lg italic text-[#57534E] text-center leading-relaxed">
                    What dogs communicate
                  </p>
                  <p className="font-['Fraunces'] text-base md:text-lg italic text-[#57534E] text-center leading-relaxed">
                    without saying a thing.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Text */}
          <div 
            ref={ref}
            className={`order-1 md:order-2 text-center md:text-left transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="font-['Fraunces'] text-3xl md:text-4xl lg:text-5xl font-light leading-tight text-[#1C1917] mb-2">
              Dogs don't perform.
            </p>
            <p className="font-['Fraunces'] text-3xl md:text-4xl lg:text-5xl font-light leading-tight text-[#1C1917]">
              They reflect.
            </p>
            
            <div className="mt-6 md:mt-8 space-y-2">
              <p className="text-lg md:text-xl leading-relaxed text-[#57534E] font-light">
                What if what you're seeing isn't a behavior problem?
              </p>
              <p className="text-lg md:text-xl leading-relaxed text-[#57534E] font-light">
                What if it's a message?
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Permission Section
const PermissionSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  
  return (
    <section 
      data-testid="permission-section" 
      className="py-8 md:py-10 bg-[#FFFCF8] relative"
    >
      <div className="container mx-auto px-6">
        <div 
          ref={ref}
          className={`max-w-2xl mx-auto text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="space-y-3">
            <p className="font-['Fraunces'] text-2xl md:text-3xl font-light leading-relaxed text-[#1C1917]">
              You've been the steady one.
            </p>
            <p className="font-['Fraunces'] text-2xl md:text-3xl font-light leading-relaxed text-[#1C1917]">
              The one who holds it together.
            </p>
            <p className="font-['Fraunces'] text-2xl md:text-3xl font-light leading-relaxed text-[#1C1917]">
              The one who swallows the 'no' to keep the peace.
            </p>
          </div>
          
          <div className="mt-6 space-y-2">
            <p className="text-lg md:text-xl leading-relaxed text-[#57534E] font-light">
              Your dog isn't broken.
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-[#57534E] font-light">
              And neither are you.
            </p>
          </div>
          
          <p className="mt-6 text-lg md:text-xl leading-relaxed text-[#C25E00] font-normal">
            Something in you already knows this isn't just about the dog.
          </p>
        </div>
      </div>
    </section>
  );
};

// Offer Section - Plants image in circle
const OfferSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  
  return (
    <section 
      data-testid="offer-section" 
      className="py-8 md:py-10 bg-[#F2ECE4] relative overflow-hidden"
    >
      <div className="container mx-auto px-6">
        <div className="relative z-10 max-w-3xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
            {/* Plants image in circle */}
            <div className="md:col-span-2 flex justify-center">
              <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-4 border-white shadow-xl">
                <img 
                  src={IMAGES.plants}
                  alt="Woman with Weimaraner in garden"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            
            {/* Text */}
            <div 
              ref={ref}
              className={`md:col-span-3 text-center md:text-left transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <h2 className="font-['Fraunces'] text-2xl md:text-3xl font-light tracking-tight text-[#1C1917]">
                What Your Dog Is Telling You
              </h2>
              <p className="mt-1 italic text-base text-[#57534E]">
                A short listening practice
              </p>
              
              <div className="mt-4 space-y-3">
                <p className="text-base md:text-lg leading-relaxed text-[#57534E] font-light">
                  An audio reflection designed to help you notice.
                </p>
                <p className="text-base md:text-lg leading-relaxed text-[#57534E] font-light">
                  Without fixing, correcting, or interpreting.
                </p>
                
                <div className="space-y-1 pt-1">
                  <p className="text-base md:text-lg leading-relaxed text-[#1C1917] font-light">
                    One practice. One moment of attention.
                  </p>
                  <p className="text-base md:text-lg leading-relaxed text-[#1C1917] font-light">
                    That's it.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section - Editorial magazine "inside page" matching size of cover above
const FinalCTASection = () => {
  return (
    <section 
      data-testid="final-cta-section" 
      className="py-8 md:py-10 bg-[#F9F5F0] relative overflow-hidden"
    >
      <div className="container mx-auto px-6">
        <div className="flex flex-col items-center">
          {/* Listen In button first */}
          <a
            href={HELLO_AUDIO_LINK}
            data-testid="final-cta-button"
            className="cta-button bg-[#C25E00] text-[#FFFCF8] hover:bg-[#A04D00] rounded-full px-8 py-3.5 text-lg font-medium shadow-lg inline-block"
          >
            Listen In
          </a>
          
          <p className="mt-2 text-sm text-[#A8A29E]">
            No spam. A short audio.
          </p>
          
          {/* Editorial Magazine - Inside page (same size as cover above) */}
          <div className="mt-8 relative max-w-[280px] md:max-w-[320px]">
            {/* Magazine frame - same as cover */}
            <div className="absolute -inset-3 bg-[#1C1917] rounded-sm" />
            
            {/* Inner magazine content - article page style */}
            <div className="relative bg-[#F2ECE4] p-3">
              {/* Article title - magazine style */}
              <div className="text-center mb-4 pt-2">
                <p className="font-['Fraunces'] text-2xl md:text-3xl font-light text-[#1C1917] leading-tight italic">
                  What settles in you,
                </p>
                <p className="font-['Fraunces'] text-2xl md:text-3xl font-light text-[#1C1917] leading-tight italic">
                  settles in them.
                </p>
                {/* Decorative line under title */}
                <div className="mt-3 mx-auto w-16 h-px bg-[#C25E00]" />
              </div>
              
              {/* Magazine image - larger, real photo feel with retro vibe */}
              <div className="relative">
                <img 
                  src={IMAGES.walkingTransparent}
                  alt="Jt Clough walking with Weimaraner"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
              
              {/* Magazine footer with button */}
              <div className="mt-4 pt-3 border-t border-[#1C1917]/20 text-center">
                <a
                  href={HELLO_AUDIO_LINK}
                  data-testid="magazine-cta-button"
                  className="inline-block bg-[#1C1917] text-[#F2ECE4] hover:bg-[#C25E00] px-6 py-2 text-sm font-['Fraunces'] tracking-wider uppercase transition-colors duration-300"
                >
                  Listen In
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Section
const FooterSection = () => {
  return (
    <footer 
      data-testid="footer-section" 
      className="py-6 md:py-8 bg-[#F2ECE4]"
    >
      <div className="container mx-auto px-6 text-center">
        <p className="font-['Fraunces'] text-base md:text-lg font-light text-[#57534E]">
          Life Well Played | Jt Clough
        </p>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="landing-page">
      <div className="texture-overlay" aria-hidden="true" />
      
      <Header />
      <HeroSection />
      <NoticingSection />
      <MirrorSection />
      <PermissionSection />
      <OfferSection />
      <FinalCTASection />
      <FooterSection />
    </div>
  );
}

export default App;
